import { useState, type ReactNode } from "react";
import { useQueries } from "@tanstack/react-query";
import { Buton, DurumRozeti, Ikon, cn, type IkonAdi } from "@koclukpanel/ui";
import { ekranYolu } from "./adres";
import {
  bildirimleriGetir,
  duyurulariGetir,
  gorevlerimiGetir,
  isListesiGetir,
  kendiProfilim,
  kontenjanim,
  mesajKutum,
  notlarim,
  oturumlarim,
  type IsKalemi,
} from "./api";

/**
 * KOÇ ANASAYFA ÖZETİ — UNİKOÇ Home-Dashboard (sürüm 9a, iç düzen turu L1).
 *
 * ================================================================
 * UNİKOÇ SIRASI: DUYURU BANDI → ALTI RENKLİ KART → ALT ŞERİT
 * ================================================================
 * Referans (`sayfalar/Home-Dashboard*`): üstte açılır "Duyuru ve Hatırlatmalar" bandı, altında
 * üçerli iki sıra kart (profil · analiz · kontenjan / son not · görevler · mesaj; her kartın üstü
 * pastel tondan beyaza akar), en altta küçük liste kartları (koç bildirimleri · eksik veli
 * raporları · son oturumlar). İş listesi (`IsListesi`) bu bölümün ALTINDA kalır, kaybolmaz.
 *
 * ================================================================
 * HER KART KENDİ İZNİYLE; İZNİ OLMAYANA İSTEK GİTMİYOR
 * ================================================================
 *  - kontenjan   → kimlik tabanlı (koç kaydı yoksa sunucu `null`; kart çizilmez)
 *  - görevler    → `operasyon.gorev.kendi`
 *  - duyurular   → `operasyon.duyuru.goruntule` (sunucu hedef kitleye göre zaten süzüyor)
 *  - son not     → kişisel not, yalnız yazana; BAŞLIK ve tarih gösterilir, İÇERİK ASLA (ana ekran
 *                  omuz üstünden görülür). Görünüm modunda sunucu reddeder; kart sessizce çekilir.
 *  - mesaj       → `mesaj.yazisma.goruntule`
 *  - iş listesi  → `analiz.ogrenci.goruntule` (aynı sorgu anahtarı: `IsListesi` ile tek istek)
 *  - profil, bildirim, oturum → `@KimlikGerekli`, yalnız kendi kaydı
 *
 * Hata/yetkisizlik KARTI GİZLER, "0" yazmaz: "hiç görev yok" ile "bakılamadı" aynı şey değil.
 * Hiçbir kart veri getirmediyse bileşen `null` döner (iş listesi tek başına kalır).
 *
 * UNİKOÇ'ta olup bizde VERİSİ OLMAYAN alanlar eklenmedi: profil fotoğrafı (baş harf dairesi),
 * "Mevcut Puanınız", planlanan ücretsiz tanışma kartı, başarısız giriş denemeleri.
 */

type Izinler = Record<string, string>;

const gunMetni = (iso: string) =>
  new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
const anMetni = (iso: string) =>
  new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Kartın düğmesi ilgili ekranı açar.
 *
 * Ekran durumu `Uygulama`da; bu bileşene gezinme geri çağrısı verilmiyor ve kabuğa dokunulmuyor.
 * Panelin KENDİ adres düzeni kullanılıyor: adres yazılıp `popstate` atılınca `Uygulama` aynı
 * ekrana geçer (adres.ts). İzin kontrolü yine ekranın kendisinde ve sunucuda; burada yalnız
 * izni görünen kartlara düğme çiziliyor.
 */
function ekranaGit(anahtar: string): void {
  const yol = ekranYolu(anahtar);
  if (window.location.pathname !== yol) window.history.pushState(null, "", yol);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/** Kontenjan doluluğu: kota aşıldıysa kritik, %90 ve üstü dikkat. */
function kontenjanHali(aktif: number, kota: number): "iyi" | "dikkat" | "kritik" {
  if (aktif > kota) return "kritik";
  return aktif / kota >= 0.9 ? "dikkat" : "iyi";
}
const KONTENJAN_ETIKETI = { iyi: "İyi", dikkat: "Dikkat", kritik: "Kritik" } as const;
const KONTENJAN_ROZETI = { iyi: "tamamlandi", dikkat: "bekliyor", kritik: "eksik" } as const;

/*
  Kart tonları UNİKOÇ'tan: lavanta, mavi, yeşil / krem, pembe, gül. Gün paletinin pastelleri
  aynı aileden ve zaten ölçülmüş belirteçler — yeni renk uydurulmadı. Sınıflar TAM yazılıyor
  (Tailwind kaynağı metin olarak tarıyor).
*/
const TON = {
  lavanta: "from-gun-cum",
  mavi: "from-gun-sal",
  yesil: "from-gun-car",
  krem: "from-gun-per",
  pembe: "from-gun-paz",
  gul: "from-gun-pzt",
} as const;

function TonKarti({
  ton,
  ikon,
  baslik,
  children,
  eylem,
  etiket,
}: {
  ton: keyof typeof TON;
  ikon?: IkonAdi;
  baslik: ReactNode;
  children?: ReactNode;
  eylem?: ReactNode;
  etiket: string;
}) {
  return (
    <section
      aria-label={etiket}
      className={cn(
        "border-mor-kenar flex flex-col items-center gap-2 rounded-[1.5rem] border p-6 text-center",
        "to-notr-0 bg-linear-to-b to-45% shadow-(--golge-unk-kart)",
        TON[ton],
      )}
    >
      {ikon && <Ikon ad={ikon} className="text-mor-500 size-10" />}
      <h3 className="text-metin-koyu text-xl leading-tight font-bold">{baslik}</h3>
      {children}
      {eylem && <div className="mt-auto pt-3">{eylem}</div>}
    </section>
  );
}

/** Alt şeritteki küçük liste kartı: başlık + sayaç rozeti + içerik. */
function ListeKarti({
  ikon,
  baslik,
  sayi,
  sayiTonu = "notr",
  children,
  eylem,
}: {
  ikon: IkonAdi;
  baslik: string;
  sayi?: number;
  sayiTonu?: "notr" | "eksik";
  children: ReactNode;
  eylem?: ReactNode;
}) {
  return (
    <section
      aria-label={baslik}
      className="bg-notr-0 border-mor-kenar flex flex-col gap-3 rounded-[1.5rem] border p-5 shadow-(--golge-unk-kart)"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-metin-koyu flex items-center gap-2 text-lg leading-tight font-bold">
          <Ikon ad={ikon} className="text-metin-koyu size-5 shrink-0" />
          {baslik}
        </h3>
        {sayi !== undefined && (
          <span
            aria-label={`${sayi} kayıt`}
            className={cn(
              "text-notr-0 inline-grid min-w-6 shrink-0 place-items-center rounded-lg px-1.5 text-xs font-bold",
              sayiTonu === "eksik" && sayi > 0 ? "bg-eksik-nokta" : "bg-notr-600",
            )}
          >
            {sayi}
          </span>
        )}
      </div>
      {children}
      {eylem && <div className="mt-auto flex justify-end">{eylem}</div>}
    </section>
  );
}

export function KocOzeti({
  izinler,
  uzmanMi,
  onOgrenciSec,
}: {
  izinler: Izinler;
  uzmanMi: boolean;
  /** Eksik veli raporu satırı o öğrencinin rapor ekranını açar (iş listesiyle aynı yol). */
  onOgrenciSec?: (ogrenciId: string, ogrenciAdi: string, tur: IsKalemi["tur"]) => void;
}) {
  const gorevIzni = "operasyon.gorev.kendi" in izinler;
  const duyuruIzni = "operasyon.duyuru.goruntule" in izinler;
  const mesajIzni = "mesaj.yazisma.goruntule" in izinler;
  const isIzni = uzmanMi && "analiz.ogrenci.goruntule" in izinler;
  const ortak = { retry: false, staleTime: 60_000 } as const;

  const [kota, gorev, duyuru, not, profil, mesaj, isler, bildirim, oturum] = useQueries({
    queries: [
      { queryKey: ["koc-ozeti", "kontenjan"], queryFn: kontenjanim, enabled: uzmanMi, ...ortak },
      {
        queryKey: ["koc-ozeti", "gorev"],
        queryFn: () => gorevlerimiGetir(),
        enabled: gorevIzni,
        ...ortak,
      },
      {
        queryKey: ["koc-ozeti", "duyuru"],
        queryFn: duyurulariGetir,
        enabled: duyuruIzni,
        ...ortak,
      },
      { queryKey: ["koc-ozeti", "not"], queryFn: () => notlarim(), enabled: uzmanMi, ...ortak },
      // Anahtar kabuğun profil bloğuyla AYNI (`["profilim"]`): ikinci istek atılmıyor.
      {
        queryKey: ["profilim"],
        queryFn: kendiProfilim,
        enabled: uzmanMi,
        retry: false,
        staleTime: 5 * 60_000,
      },
      { queryKey: ["koc-ozeti", "mesaj"], queryFn: mesajKutum, enabled: mesajIzni, ...ortak },
      // Anahtar `IsListesi` ile AYNI: aynı veri, tek istek.
      { queryKey: ["is-listesi"], queryFn: isListesiGetir, enabled: isIzni, retry: false },
      {
        queryKey: ["koc-ozeti", "bildirim"],
        queryFn: () => bildirimleriGetir(true),
        enabled: uzmanMi,
        ...ortak,
      },
      { queryKey: ["koc-ozeti", "oturum"], queryFn: oturumlarim, enabled: uzmanMi, ...ortak },
    ],
  });

  const kontenjan = kota.data ?? null;
  const bekleyenGorev = gorev.data
    ? gorev.data.gorevler.filter((g) => g.status === "PENDING_ACCEPT" || g.status === "IN_PROGRESS")
        .length
    : null;
  const duyurular = duyuru.data ? duyuru.data.duyurular : null;
  // Liste `guncelleme` azalan; yine de en yeniyi kendimiz seçiyoruz (sıralama sunucu sözü değil).
  const sonNot = not.data
    ? ([...not.data].sort((a, b) => b.olusturma.localeCompare(a.olusturma))[0] ?? null)
    : null;
  const veriGirmeyen = isler.data
    ? isler.data.filter((k) => k.tur === "VERI_GIRMEYEN").length
    : null;
  const eksikVeli = isler.data ? isler.data.filter((k) => k.tur === "VELI_RAPORU_BEKLIYOR") : null;

  const gosterilecek =
    kontenjan !== null ||
    bekleyenGorev !== null ||
    duyurular !== null ||
    not.data !== undefined ||
    profil.data !== undefined;
  if (!gosterilecek) return null;

  const basHarf = profil.data
    ? `${profil.data.firstName.charAt(0)}${profil.data.lastName.charAt(0)}`.toLocaleUpperCase(
        "tr-TR",
      )
    : "";

  return (
    <section aria-label="Özet" className="mb-6 flex flex-col gap-6">
      {duyurular !== null && <DuyuruBandi duyurular={duyurular} />}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {profil.data && (
          <TonKarti ton="lavanta" etiket="Profil" baslik={null}>
            <span
              aria-hidden="true"
              className="bg-mor-50 text-mor-700 ring-mor-kesikli grid size-24 place-items-center rounded-full text-3xl font-bold ring-4"
            >
              {basHarf}
            </span>
            <p className="text-metin-koyu text-xl leading-tight font-bold">
              {profil.data.firstName} {profil.data.lastName.toLocaleUpperCase("tr-TR")}
            </p>
            {profil.data.roller[0] && (
              <p className="text-metin-ikincil text-sm">{profil.data.roller[0]}</p>
            )}
            <div className="pt-3">
              <Buton onClick={() => ekranaGit("hesap")}>Profilim</Buton>
            </div>
          </TonKarti>
        )}

        {isIzni && (
          <TonKarti
            ton="mavi"
            ikon="analiz"
            etiket="Analiz ve Raporlama"
            baslik="Analiz ve Raporlama"
            eylem={<Buton onClick={() => ekranaGit("haftalik-analiz")}>Analiz ve Raporlama</Buton>}
          >
            <p className="text-metin-ikincil text-base">
              Öğrenci başarı raporlarını analiz edin ve inceleyin.
            </p>
            {veriGirmeyen !== null && (
              <div className="border-mor-kesikli mt-2 flex w-full items-center gap-3 rounded-xl border border-dashed p-3 text-left">
                <p className="text-metin-koyu min-w-0 flex-1 text-sm">
                  <span className="text-metin-ikincil block text-xs">
                    Öğrencilerde eksik veri girişi
                  </span>
                  <span
                    className={cn(
                      "text-notr-0 mr-1 inline-grid min-w-7 place-items-center rounded-md px-1.5 text-xs font-bold",
                      veriGirmeyen > 0 ? "bg-eksik-nokta" : "bg-tamam-nokta",
                    )}
                  >
                    {veriGirmeyen}
                  </span>
                  <strong>kayıt eksik görünüyor.</strong>
                </p>
                <Buton
                  gorunum="kesikli"
                  onClick={() =>
                    document.getElementById("yapilacaklar")?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Görüntüle
                </Buton>
              </div>
            )}
          </TonKarti>
        )}

        {kontenjan && (
          <TonKarti
            ton="yesil"
            ikon="profil"
            etiket="Kontenjan"
            baslik={kontenjan.kota > 0 ? `${kontenjan.aktifOgrenci} / ${kontenjan.kota}` : "—"}
            eylem={
              "kisi.ogrenci.goruntule" in izinler ? (
                <Buton onClick={() => ekranaGit("ogrenciler")}>Öğrencilerime Git</Buton>
              ) : undefined
            }
          >
            {kontenjan.kota > 0 ? (
              <>
                <p className="text-metin-ikincil text-base">Öğrenciniz Mevcut</p>
                <DurumRozeti
                  durum={KONTENJAN_ROZETI[kontenjanHali(kontenjan.aktifOgrenci, kontenjan.kota)]}
                >
                  {KONTENJAN_ETIKETI[kontenjanHali(kontenjan.aktifOgrenci, kontenjan.kota)]}
                </DurumRozeti>
              </>
            ) : (
              // Kota tanımsız (0): "0 / 0" yazmak ölçülmemiş şeyi sıfır göstermek olurdu.
              <p className="text-metin-ikincil text-base">Kota tanımlanmamış</p>
            )}
          </TonKarti>
        )}

        {not.data !== undefined && (
          <TonKarti
            ton="krem"
            ikon="duzenle"
            etiket="Son notunuz"
            baslik="Son Notunuz"
            eylem={<Buton onClick={() => ekranaGit("notlarim")}>Notlarım</Buton>}
          >
            {sonNot ? (
              <>
                <p className="text-metin-koyu text-base font-bold uppercase">{sonNot.baslik}</p>
                <p className="text-metin-ikincil text-sm">
                  Oluşturulma Tarihi: {anMetni(sonNot.olusturma)}
                </p>
              </>
            ) : (
              <p className="text-metin-ikincil text-base">Henüz not yok.</p>
            )}
          </TonKarti>
        )}

        {bekleyenGorev !== null && (
          <TonKarti
            ton="pembe"
            ikon="gorevler"
            etiket="Bekleyen görevler"
            baslik={
              bekleyenGorev === 0
                ? "Tüm Görevleriniz Tamamlandı"
                : `${bekleyenGorev} Bekleyen Görev`
            }
            eylem={
              duyuruIzni ? (
                <Buton onClick={() => ekranaGit("operasyon")}>Görevlerim</Buton>
              ) : undefined
            }
          >
            <p className="text-metin-ikincil text-base">
              {bekleyenGorev === 0
                ? "Görevlerinizi görüntüleyin ve yönetin."
                : "Kabul bekleyen ve işlemdeki görevleriniz var."}
            </p>
          </TonKarti>
        )}

        {mesaj.data && (
          <TonKarti
            ton="gul"
            ikon="sohbet"
            etiket="Mesajlar"
            baslik={
              mesaj.data.toplamOkunmamis === 0
                ? "Yeni Mesaj Yok"
                : `${mesaj.data.toplamOkunmamis} Yeni Mesaj`
            }
            eylem={
              mesaj.data.toplamOkunmamis > 0 ? (
                <Buton onClick={() => ekranaGit("mesajlar")}>Mesajlar</Buton>
              ) : undefined
            }
          >
            <p className="text-metin-ikincil text-base">
              {mesaj.data.toplamOkunmamis === 0
                ? "Henüz yeni bir mesajınız yok."
                : "Okunmamış mesajlarınız var."}
            </p>
          </TonKarti>
        )}
      </div>

      {(bildirim.data || eksikVeli || oturum.data) && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {bildirim.data && (
            <ListeKarti
              ikon="bildirim"
              baslik="Koç Bildirimleri"
              sayi={bildirim.data.bildirimler.length}
              eylem={
                <Buton gorunum="kesikli" onClick={() => ekranaGit("bildirimler")}>
                  Bildirim Sayfasına Git
                </Buton>
              }
            >
              {bildirim.data.bildirimler.length === 0 ? (
                <p className="border-mor-kenar text-metin-ikincil flex items-center gap-2 rounded-xl border border-dashed p-3 text-sm">
                  <Ikon ad="tamam" className="size-4" />
                  Yeni bildiriminiz yok
                </p>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {bildirim.data.bildirimler.slice(0, 3).map((b) => (
                    <li key={b.id} className="text-metin-koyu truncate text-sm">
                      {b.title}
                    </li>
                  ))}
                </ul>
              )}
            </ListeKarti>
          )}

          {eksikVeli && (
            <ListeKarti
              ikon="rapor"
              baslik="Eksik Veli Raporları"
              sayi={eksikVeli.length}
              sayiTonu="eksik"
            >
              {eksikVeli.length === 0 ? (
                <p className="text-metin-ikincil text-sm">Bekleyen veli raporu yok.</p>
              ) : (
                <ul className="divide-mor-kenar flex flex-col divide-y">
                  {eksikVeli.slice(0, 3).map((k) => (
                    <li key={`${k.ogrenciId}-${k.baslik}`}>
                      <button
                        type="button"
                        disabled={!onOgrenciSec}
                        onClick={() => onOgrenciSec?.(k.ogrenciId, k.ogrenciAdi, k.tur)}
                        className={cn(
                          "min-h-dokunma flex w-full items-center gap-2 text-left text-sm",
                          "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2",
                          "enabled:hover:text-mor-700",
                        )}
                      >
                        <Ikon ad="uyari" className="text-eksik-nokta size-4 shrink-0" />
                        <span className="min-w-0 truncate">
                          <strong className="text-metin-koyu">{k.ogrenciAdi}</strong>
                          <span className="text-metin-ikincil"> — {k.baslik}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {eksikVeli.length > 3 && (
                <p className="text-metin-ikincil text-xs">
                  +{eksikVeli.length - 3} rapor daha — aşağıdaki listede.
                </p>
              )}
            </ListeKarti>
          )}

          {oturum.data && (
            <ListeKarti ikon="guvenlik" baslik="Son Oturumlarım">
              <p className="text-metin-ikincil text-sm">
                Hesabınızda açık olan son oturumlar listelenmiştir.
              </p>
              <ul className="divide-mor-kenar flex flex-col divide-y border-dashed">
                {[...oturum.data]
                  .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                  .slice(0, 5)
                  .map((o) => (
                    <li
                      key={o.id}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm"
                    >
                      <span className="text-metin-ikincil">{anMetni(o.createdAt)}</span>
                      <DurumRozeti durum="tamamlandi">
                        {o.suAnki ? "Bu oturum" : "Başarılı giriş"}
                      </DurumRozeti>
                      {o.ipAddress && (
                        <span className="text-metin-ikincil w-full text-xs">IP: {o.ipAddress}</span>
                      )}
                    </li>
                  ))}
              </ul>
            </ListeKarti>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * DUYURU VE HATIRLATMALAR — UNİKOÇ bandı. İlk duyuru açık başlar, ötekiler başlıkla durur;
 * başlığa basınca metin açılır. Okundu işareti Görevlerim → Duyurular sekmesinde kalıyor:
 * ana ekranda bir duyuruyu açmak "okudum" demek değil.
 */
function DuyuruBandi({
  duyurular,
}: {
  duyurular: { id: string; title: string; body: string; publishAt: string }[];
}) {
  const [acik, setAcik] = useState<string | null>(duyurular[0]?.id ?? null);
  return (
    <section
      aria-label="Duyuru ve Hatırlatmalar"
      className="bg-notr-0 border-mor-kenar overflow-hidden rounded-[1.5rem] border shadow-(--golge-unk-kart)"
    >
      <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3">
        <h2 className="text-metin-koyu flex items-center gap-2 text-2xl font-bold">
          <Ikon ad="duyuru" className="text-mor-500 size-7" />
          Duyuru ve Hatırlatmalar
        </h2>
        <span className="text-metin-ikincil shrink-0 text-sm">{duyurular.length} duyuru</span>
      </div>
      {duyurular.length === 0 ? (
        <p className="text-metin-ikincil px-5 pb-5 text-sm">Duyuru yok.</p>
      ) : (
        <ul className="divide-mor-kenar divide-y divide-dashed">
          {duyurular.slice(0, 5).map((d) => {
            const buAcik = acik === d.id;
            return (
              <li key={d.id}>
                <button
                  type="button"
                  aria-expanded={buAcik}
                  onClick={() => setAcik(buAcik ? null : d.id)}
                  className={cn(
                    "min-h-dokunma flex w-full items-center gap-3 px-5 py-3 text-left",
                    "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2",
                    buAcik ? "bg-mor-50" : "hover:bg-mor-50",
                  )}
                >
                  <Ikon ad="bilgi" className="text-mor-500 size-5 shrink-0" />
                  <span className="text-metin-koyu min-w-0 flex-1 truncate font-semibold">
                    {d.title}
                  </span>
                  <span className="border-mor-kenar text-metin-ikincil shrink-0 rounded-full border px-3 py-0.5 text-xs font-semibold max-sm:hidden">
                    {gunMetni(d.publishAt)}
                  </span>
                  <Ikon
                    ad="asagiOk"
                    className={cn(
                      "text-metin-ikincil size-4 shrink-0 transition-transform",
                      buAcik && "rotate-180",
                    )}
                  />
                </button>
                {buAcik && d.body && (
                  <p className="text-metin-koyu px-5 pt-1 pb-4 text-sm whitespace-pre-line">
                    {d.body}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
